// slotGenerator.js
// Slot ayarlarından (system_config) bir gün için slot dizisi üretir.
//
// Pattern (mevcut sistemle uyumlu):
//   - 2 slot art arda (boşluksuz blok)
//   - blok bitince break_duration dk mola
//   - öğle arasına denk gelen slot atlanır ve öğle sonuna zıplanır
//   - öğle bitene kadar mola eklenmez (mola öğlede erir)
//   - day_end'e yaklaşınca artık slot sığmıyorsa durur

const DAY_START = '08:00'
const DAY_END = '18:30'
const SLOTS_PER_BLOCK = 2

function timeToMinutes(hhmm) {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

function minutesToHHMM(min) {
  const h = Math.floor(min / 60)
  const m = min % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/**
 * Bir gün için slot dizisi üretir.
 * @param {Object} params
 * @param {number} params.slotDuration - dakika
 * @param {number} params.breakDuration - blok sonrası mola dakika
 * @param {string} params.lunchStart - "HH:MM"
 * @param {string} params.lunchEnd - "HH:MM"
 * @param {Object} [opts]
 * @param {string} [opts.dayStart] - default "08:00"
 * @param {string} [opts.dayEnd] - default "18:30"
 * @returns {Array<{id: string, time: string, disabled: boolean}>}
 */
export function generateDaySlots(params, opts = {}) {
  const { slotDuration, breakDuration, lunchStart, lunchEnd } = params
  const dayStart = opts.dayStart || DAY_START
  const dayEnd = opts.dayEnd || DAY_END

  const slots = []
  let cursor = timeToMinutes(dayStart)
  const endTime = timeToMinutes(dayEnd)
  const lunchStartMin = timeToMinutes(lunchStart)
  const lunchEndMin = timeToMinutes(lunchEnd)

  while (cursor + slotDuration <= endTime) {
    let blockInterrupted = false

    for (let i = 0; i < SLOTS_PER_BLOCK; i++) {
      const slotEnd = cursor + slotDuration
      if (slotEnd > endTime) break

      // Slot öğle arasına çakışıyor mu?
      if (cursor < lunchEndMin && slotEnd > lunchStartMin) {
        cursor = lunchEndMin
        blockInterrupted = true
        break
      }

      const startStr = minutesToHHMM(cursor)
      const endStr = minutesToHHMM(slotEnd)
      slots.push({
        id: `${startStr}-${endStr}`,
        time: `${startStr.replace(':', '.')}-${endStr.replace(':', '.')}`,
        disabled: false
      })
      cursor = slotEnd
    }

    if (!blockInterrupted) {
      cursor += breakDuration
      // Mola öğle içine düşerse öğle sonuna zıpla (mola öğlede erir)
      if (cursor >= lunchStartMin && cursor < lunchEndMin) {
        cursor = lunchEndMin
      }
    }
  }

  // Öğle arasını disabled slot olarak kronolojik sıraya insert et
  const lunchSlot = {
    id: `${lunchStart}-${lunchEnd}`,
    time: `${lunchStart.replace(':', '.')}-${lunchEnd.replace(':', '.')}`,
    disabled: true
  }
  const insertIdx = slots.findIndex(s => timeToMinutes(s.id.split('-')[0]) > lunchStartMin)
  if (insertIdx === -1) slots.push(lunchSlot)
  else slots.splice(insertIdx, 0, lunchSlot)

  return slots
}

/**
 * Hafta içi slotları — settings object'inden ilgili alanları alır.
 * @param {Object} settings - backend'den gelen slot ayarları
 */
export function generateWeekdaySlots(settings) {
  if (!settings) return []
  return generateDaySlots({
    slotDuration: settings.slot_duration_weekday,
    breakDuration: settings.break_duration_weekday,
    lunchStart: settings.lunch_start_weekday,
    lunchEnd: settings.lunch_end_weekday
  }, {
    dayStart: settings.day_start_weekday,
    dayEnd: settings.day_end_weekday
  })
}

/**
 * Hafta sonu slotları.
 */
export function generateWeekendSlots(settings) {
  if (!settings) return []
  return generateDaySlots({
    slotDuration: settings.slot_duration_weekend,
    breakDuration: settings.break_duration_weekend,
    lunchStart: settings.lunch_start_weekend,
    lunchEnd: settings.lunch_end_weekend
  }, {
    dayStart: settings.day_start_weekend,
    dayEnd: settings.day_end_weekend
  })
}
